class Tabs {

  /**
   * Adds tab functionality to given tablist.
   *
   * @param {HTMLElement} element Element with [role="tablist"].
   */
  constructor(element) {
    this.tablistElement = element;
    this.tabs = [];
    this._listeners = [];

    this._clickEventListener = this._handleTabClickEvent.bind(this);
    this._keyEventListener = this._handleTabKeyEvent.bind(this);
    this.reinit();
  }

  _handleTabClickEvent(e) {
    this.showTab(e.target.id);
  }

  _handleTabKeyEvent(e) {
    const isVertical = this.tablistElement.getAttribute('aria-orientation') === 'vertical';
    const oldIndex = this.tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
    if (e.key === 'ArrowDown' && isVertical || e.key === 'ArrowRight') {
      this.showTab(this.tabs[oldIndex < this.tabs.length - 1 ? oldIndex + 1 : 0].id);
    }
    else if (e.key === 'ArrowUp' && isVertical || e.key === 'ArrowLeft') {
      this.showTab(this.tabs[oldIndex - 1 >= 0 ? oldIndex - 1 : this.tabs.length - 1].id);
    }
    else if (e.key === 'Home') {
      this.showTab(this.tabs[0].id);
    }
    else if (e.key === 'End') {
      this.showTab(this.tabs[this.tabs.length - 1].id);
    }
  }

  _trigger(type, data) {
    this._listeners.filter(item => item.type === type).forEach(item => {
      item.callback.apply(this, data);
    });
  }

  /**
   * Register event listeners.
   *
   * @param {String} type Event type.
   * @param {Function} callback Callback function.
   */
  on(type, callback) {
    this._listeners.push({ type: type, callback: callback });
  }

  /**
   * Updates dom references and removes/attaches listeners.
   */
  reinit() {
    this.tabs.forEach(tab => {
      tab.removeEventListener('click', this._clickEventListener);
      tab.removeEventListener('keydown', this._keyEventListener);
    });
    this.tabs = Array.from(this.tablistElement.querySelectorAll('[role="tab"]'));
    this.tabs.forEach(tab => {
      tab.addEventListener('click', this._clickEventListener);
      tab.addEventListener('keydown', this._keyEventListener);
    });
  }

  /**
   * Get currently active tab.
   *
   * @returns {HTMLElement}
   */
  getActiveTab() {
    return this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true');
  }

  /**
   * Activates given tab.
   *
   * @param {String} id Tab html id.
   */
  showTab(id) {
    const activeTab = this.getActiveTab();
    if (activeTab.id === id) {
      return;
    }

    this.tabs.forEach(tab => {
      const isActiveTab = tab.id === id;
      tab.setAttribute('aria-selected', isActiveTab ? 'true' : 'false');
      tab.setAttribute('tabindex', isActiveTab ? '0' : '-1');
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) {
        if (isActiveTab) {
          panel.removeAttribute('aria-hidden');
        }
        else {
          panel.setAttribute('aria-hidden', 'true');
        }
      }
    });

    const newActiveTab = this.getActiveTab();
    newActiveTab.focus();

    this._trigger('change', [newActiveTab, activeTab]);
  }

}

export default Tabs;
